import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"

import { AlertDialog, Button, Switch } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { SlackNotificationField } from "./notification-status"
import type { PersonalNotificationSettingsProps } from "./types"
import { analytics } from "@/lib/analytics"
import { mutateOk, useZero } from "@/lib/zero"
import { docsDigestToggleTitle } from "@/screens/app/helpCenter/docs-proposal-cadence"
import { SettingItem } from "../settings-switches/setting-item"
import { SettingsContainer } from "../settings-switches/settings-container"
import {
	buildToggleAllPayload,
	isSlackNotificationsEnabled,
	SLACK_NOTIFICATION_FIELDS,
} from "./notification-status"

export function PersonalSlackNotificationSection({
	data,
	docsProposalCadence,
}: PersonalNotificationSettingsProps) {
	const [isDisconnectOpen, setIsDisconnectOpen] = useState(false)
	const settings = data.memberships?.personalSlackNotificationSettings
	const personalSlackNotificationsId = settings?.id ?? ""
	const zero = useZero()
	const navigate = useNavigate()

	const handleDisconnect = async () => {
		await mutateOk(
			zero.mutate(
				mutators.personalSlackNotificationSettings.delete({
					id: personalSlackNotificationsId,
				}),
			),
		)
		analytics.track("disconnect-slack-notification-settings")
		void navigate({ to: "/settings/notifications", replace: true })
	}

	const updateSettings = async (
		values: Partial<Record<SlackNotificationField, boolean>>,
	) => {
		await mutateOk(
			zero.mutate(
				mutators.personalSlackNotificationSettings.update({
					id: personalSlackNotificationsId,
					...values,
				}),
			),
		)
		analytics.track("update-slack-notification-settings")
	}

	const handleToggle = async (field: SlackNotificationField, value: boolean) =>
		updateSettings({ [field]: value })

	const handleToggleAll = async (value: boolean) =>
		updateSettings(buildToggleAllPayload(SLACK_NOTIFICATION_FIELDS, value))

	if (!settings) return null

	return (
		<>
			<SettingsContainer>
				<SettingItem
					title="Slack notifications"
					description={
						settings.teamName
							? `Direct messages in ${settings.teamName}`
							: "Direct messages in your connected Slack workspace"
					}
					action={
						<Switch
							name="slackNotifications"
							checked={isSlackNotificationsEnabled(settings)}
							onCheckedChange={handleToggleAll}
						/>
					}
				/>
			</SettingsContainer>

			<div className="pb-3 pt-8 text-sm font-medium">
				Threads assigned to you
			</div>
			<SettingsContainer>
				<SettingItem
					title="Follow-up message"
					description="When someone follows up on your assigned threads"
					action={
						<Switch
							name="followUpMessageNotificationsEnabled"
							checked={settings.followUpMessageNotificationsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle("followUpMessageNotificationsEnabled", value)
							}}
						/>
					}
				/>

				<SettingItem
					className="pt-4"
					title="Assignment changes"
					description="When thread assignments change"
					action={
						<Switch
							name="assignmentChangesNotificationsEnabled"
							checked={settings.assignmentChangesNotificationsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle(
									"assignmentChangesNotificationsEnabled",
									value,
								)
							}}
						/>
					}
				/>

				<SettingItem
					className="pt-4"
					title="Comment created"
					description="When new comments are added to your threads"
					action={
						<Switch
							name="commentCreatedNotificationsEnabled"
							checked={settings.commentCreatedNotificationsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle("commentCreatedNotificationsEnabled", value)
							}}
						/>
					}
				/>
			</SettingsContainer>

			<div className="pb-3 pt-8 text-sm font-medium">All threads</div>
			<SettingsContainer>
				<SettingItem
					title="New thread"
					description="When new threads are created"
					action={
						<Switch
							name="newThreadNotificationsEnabled"
							checked={settings.newThreadNotificationsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle("newThreadNotificationsEnabled", value)
							}}
						/>
					}
				/>

				<SettingItem
					className="pt-4"
					title="Productlane Agent handoffs"
					description="When the Productlane Agent tells a customer the team will follow up"
					action={
						<Switch
							name="aiHandoffNotificationsEnabled"
							checked={settings.aiHandoffNotificationsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle("aiHandoffNotificationsEnabled", value)
							}}
						/>
					}
				/>

				<SettingItem
					className="pt-4"
					title="Comment mentions"
					description="When you're mentioned in comments"
					action={
						<Switch
							name="commentMentionsNotificationsEnabled"
							checked={settings.commentMentionsNotificationsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle("commentMentionsNotificationsEnabled", value)
							}}
						/>
					}
				/>

				<SettingItem
					className="pt-4"
					title="Unassigned thread replies"
					description="When someone replies to unassigned threads"
					action={
						<Switch
							name="unassignedThreadReplyNotificationsEnabled"
							checked={settings.unassignedThreadReplyNotificationsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle(
									"unassignedThreadReplyNotificationsEnabled",
									value,
								)
							}}
						/>
					}
				/>
			</SettingsContainer>

			<div className="pb-3 pt-8 text-sm font-medium">SLA</div>
			<SettingsContainer>
				<SettingItem
					title="SLA at risk"
					description="When a thread assigned to you is within a day of its SLA deadline"
					action={
						<Switch
							name="slaAtRiskNotificationsEnabled"
							checked={settings.slaAtRiskNotificationsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle("slaAtRiskNotificationsEnabled", value)
							}}
						/>
					}
				/>

				<SettingItem
					className="pt-4"
					title="SLA breached"
					description="When a thread assigned to you passes its SLA deadline without a reply"
					action={
						<Switch
							name="slaBreachedNotificationsEnabled"
							checked={settings.slaBreachedNotificationsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle("slaBreachedNotificationsEnabled", value)
							}}
						/>
					}
				/>
			</SettingsContainer>

			<div className="pb-3 pt-8 text-sm font-medium">Docs</div>
			<SettingsContainer>
				<SettingItem
					title={docsDigestToggleTitle(docsProposalCadence)}
					description="Docs your agent drafted, with accept and decline buttons in the message"
					action={
						<Switch
							name="docsProposalNotificationsEnabled"
							checked={settings.docsProposalNotificationsEnabled}
							onCheckedChange={async (value) => {
								await handleToggle("docsProposalNotificationsEnabled", value)
							}}
						/>
					}
				/>
			</SettingsContainer>

			<div className="mt-8 flex items-center justify-between gap-4">
				<span className="text-sm text-secondary">
					{settings.teamName
						? `Connected to ${settings.teamName}`
						: "Slack connected"}
				</span>
				<Button
					variant="outline"
					size="sm"
					onClick={() => setIsDisconnectOpen(true)}
				>
					Disconnect
				</Button>
			</div>

			<AlertDialog
				isOpen={isDisconnectOpen}
				onOpenChange={setIsDisconnectOpen}
				variant="positive"
				confirmText="Disconnect"
				title="Disconnect Slack?"
				description="You'll stop receiving personal Slack notifications. Reconnect any time to turn them back on."
				onCancel={() => setIsDisconnectOpen(false)}
				onConfirm={handleDisconnect}
			/>
		</>
	)
}
